import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteIcon,
  DescriptionOutlined as OffreIcon,
  EditOutlined as EditIcon,
  Inventory2Outlined as MatiereIcon,
  PaymentsOutlined as AvanceIcon,
  PendingActionsOutlined as ResteIcon,
  RestoreFromTrash as RestoreIcon,
  PrintOutlined as PrintIcon,
  Search as SearchIcon,
  TrendingUp as TotalIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import PermissionButton from '../../../components/PermissionButton';
import CustomTablePagination from '../../../components/CustomTablePagination';
import { GlassSurface } from '../../../components/GlassDashboard';
import useAlert from '../../../hooks/useAlert';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { auditLogsAPI, comptoirAPI } from '../../../services/api';
import {
  useComptoirDeletedOffres,
  useComptoirOffres,
  useComptoirStats,
  useInvalidateComptoir,
} from '../../../hooks/queries/useComptoir';
import { ComptoirOffre, ComptoirSuppressionImpact } from '../../../types/comptoir';
import OffreWizard from '../components/OffreWizard';
import SuppressionDialog from '../components/SuppressionDialog';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { COLOR, ClientAvatar, EmptyState, dh, fDate, fmtPhone, primaryBtnSx } from '../ui';

const MINI = 'Offres commerciales';

const ResteChip: React.FC<{ o: ComptoirOffre }> = ({ o }) => {
  const total = Number(o.total) || 0;
  const reste = Number(o.reste) || 0;
  const pct = total > 0 ? Math.min(100, Math.round((total - reste) / total * 100)) : 0;
  if (reste <= 0) return <Chip label="Soldée" size="small" color="success" />;
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'error.main', textAlign: 'right' }}>{dh(reste)}</Typography>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 4, borderRadius: 2, mt: 0.5, bgcolor: alpha(COLOR, 0.12), '& .MuiLinearProgress-bar': { bgcolor: COLOR } }} />
    </Box>
  );
};

const ComptoirOffres: React.FC = () => {
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('Comptoir', 'edit', MINI);
  const canAdd = hasPermission('Comptoir', 'add', MINI);
  const canSeeServices = hasPermission('Comptoir', 'view', 'Services & Projets');
  const navigate = useNavigate();
  const invalidate = useInvalidateComptoir();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<ComptoirOffre | null>(null);
  // Offre with payments / livraisons: request or Manager decision, with what it cancels
  const [suppression, setSuppression] = useState<{ offre: ComptoirOffre; impact: ComptoirSuppressionImpact } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const listQuery = useComptoirOffres({ skip: page * rowsPerPage, limit: rowsPerPage, search: debounced || undefined }, tab === 0);
  const deletedQuery = useComptoirDeletedOffres(tab === 1);
  const { data: stats, isLoading: statsLoading } = useComptoirStats();
  const offres = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  const openNew = () => { setEditing(null); setWizardOpen(true); };
  const openEdit = (o: ComptoirOffre) => { if (canEdit) { setEditing(o); setWizardOpen(true); } };

  // Devis PDF: the generator (and @react-pdf) only loads when someone prints
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const printDevis = (o: ComptoirOffre) => {
    openPreview({
      getBlob: async () => {
        const { getComptoirDevisPDFBlob } = await import('../../../utils/comptoirDevisPdfReactPdf');
        return getComptoirDevisPDFBlob(o);
      },
      fileName: `Devis_${o.reference}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ComptoirOffre', o.id, `Aperçu devis - ${o.reference}`);
  };

  const handleDelete = async (o: ComptoirOffre) => {
    let impact: ComptoirSuppressionImpact;
    try {
      impact = await comptoirAPI.suppressionImpact(o.id);
    } catch (err: any) {
      showError(extractErrorMessage(err));
      return;
    }
    if (!impact.simple) {
      setSuppression({ offre: o, impact });
      return;
    }
    showConfirm(
      `L'offre ${o.reference} de ${o.client_nom} (${dh(o.total)}) ira dans la corbeille. Son numéro ne sera jamais réutilisé ; vous pourrez la restaurer.`,
      async () => {
        try {
          await comptoirAPI.deleteOffre(o.id);
          showSuccess(`Offre ${o.reference} supprimée`);
          invalidate();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: `Supprimer l'offre ${o.reference} ?`, confirmText: 'Supprimer' },
    );
  };

  const handleRestore = async (o: ComptoirOffre) => {
    try {
      await comptoirAPI.restoreOffre(o.id);
      showSuccess(`Offre ${o.reference} restaurée`);
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const kpis = [
    { title: 'Offres', value: stats ? String(stats.count) : '…', sub: stats ? `${stats.mois_count} ce mois-ci` : '', icon: <OffreIcon />, color: COLOR },
    { title: 'Montant total', value: stats ? dh(stats.total) : '…', sub: stats ? `${dh(stats.mois_total)} ce mois-ci` : '', icon: <TotalIcon />, color: '#00897B' },
    { title: 'Encaissé', value: stats ? dh(stats.avance) : '…', sub: '', icon: <AvanceIcon />, color: '#4CAF50' },
    { title: 'Reste à encaisser', value: stats ? dh(stats.reste) : '…', sub: '', icon: <ResteIcon />, color: '#E53935' },
  ];

  const clientCell = (o: ComptoirOffre) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <ClientAvatar name={o.client_nom} size={34} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {o.client_nom || '—'}
          {o.client_surnom && <Typography component="span" variant="body2" color="text.secondary"> « {o.client_surnom} »</Typography>}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fmtPhone(o.client_telephone)}</Typography>
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR }}>Offres commerciales</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
            Une offre est enregistrée quand le client accepte : ses travaux, la matière qu'il apporte, le prix et l'avance versée.
          </Typography>
        </Box>
        <PermissionButton
          moduleName="Comptoir" operation="add" miniModuleName={MINI} variant="contained" size="large"
          startIcon={<AddIcon />} onClick={openNew} sx={{ ...primaryBtnSx, whiteSpace: 'nowrap', px: 3 }}
        >
          Nouvelle demande
        </PermissionButton>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k.title}>
            <GlassSurface accentColor={k.color} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">{k.title}</Typography>
                  {statsLoading ? <Skeleton width={120} height={36} /> : (
                    <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: k.title === 'Offres' ? undefined : 'monospace' }} noWrap>{k.value}</Typography>
                  )}
                  {k.sub && <Typography variant="caption" color="text.secondary">{k.sub}</Typography>}
                </Box>
                <Box sx={{ color: k.color, '& svg': { fontSize: 38 } }}>{k.icon}</Box>
              </Stack>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ px: 2, pt: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} TabIndicatorProps={{ sx: { bgcolor: COLOR } }}
            sx={{ '& .Mui-selected': { color: `${COLOR} !important` } }}>
            <Tab label={`Offres (${total})`} />
            <Tab label="Corbeille" />
          </Tabs>
          {tab === 0 && (
            <TextField
              size="small" placeholder="N° d'offre, client, téléphone, travail…" value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: { md: 380 }, pb: { xs: 1, md: 0 } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} aria-label="Effacer la recherche"><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Stack>
        {listQuery.isFetching && tab === 0 && <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { bgcolor: COLOR }, bgcolor: alpha(COLOR, 0.1) }} />}

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            {!listQuery.isLoading && offres.length === 0 ? (
              debounced ? (
                <EmptyState icon={<SearchIcon />} title="Aucune offre trouvée" text={`Rien ne correspond à « ${debounced} ».`}
                  action={{ label: 'Effacer la recherche', onClick: () => setSearch('') }} />
              ) : (
                <EmptyState icon={<OffreIcon />} title="Aucune offre pour l'instant"
                  text="Quand un client accepte, enregistrez sa demande : client, travaux, prix et avance."
                  action={canAdd ? { label: 'Nouvelle demande', icon: <AddIcon />, onClick: openNew } : undefined} />
              )
            ) : (
              <>
                <TableContainer sx={getModuleTableTheme('comptoir')}>
                  <Table size="small" sx={{ minWidth: 1080 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>N° d'offre</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Travaux</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Encaissé</TableCell>
                        <TableCell align="right">Reste</TableCell>
                        <TableCell align="center" width={130}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listQuery.isLoading
                        ? Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                        ))
                        : offres.map((o) => (
                          <TableRow key={o.id} hover onClick={() => openEdit(o)} sx={{ cursor: canEdit ? 'pointer' : 'default' }}>
                            <TableCell>
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap' }}>{o.reference}</Typography>
                              <Typography variant="caption" color="text.secondary">{fDate(o.date)}</Typography>
                              {o.suppression_demande && (
                                <Tooltip title={`Demandée par ${o.suppression_demande.par || '?'}${o.suppression_demande.motif ? ` — ${o.suppression_demande.motif}` : ''}`}>
                                  <Chip
                                    label="Suppression demandée" size="small" color="error" variant="outlined"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(o); }}
                                    sx={{ display: 'flex', mt: 0.5, height: 20, fontSize: 11, width: 'fit-content' }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell onClick={(e) => o.service && canSeeServices && e.stopPropagation()}>
                              {o.service ? (
                                <Tooltip title={canSeeServices ? 'Ouvrir le service' : ''}>
                                  <Chip label={o.service.reference} size="small" variant="outlined"
                                    clickable={canSeeServices}
                                    onClick={canSeeServices ? () => navigate(`/comptoir/services/${o.service!.id}`) : undefined}
                                    sx={{ fontFamily: 'monospace', fontWeight: 700, borderColor: alpha(COLOR, 0.5), color: COLOR }} />
                                </Tooltip>
                              ) : '—'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 240 }}>{clientCell(o)}</TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              <Typography variant="body2" noWrap title={o.titre}>{o.titre}</Typography>
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                                {o.travaux.map((t) => t.type_service).filter((ts, i, all) => all.indexOf(ts) === i).map((ts) => (
                                  <Chip key={ts} label={ts} size="small" sx={{ height: 20, fontSize: 11 }} />
                                ))}
                                {o.matieres.length > 0 && (
                                  <Tooltip title={o.matieres.map((m) => m.designation).join(', ')}>
                                    <Chip icon={<MatiereIcon />} label="Matière client" size="small" variant="outlined"
                                      sx={{ height: 20, fontSize: 11, borderColor: COLOR, color: COLOR, '& .MuiChip-icon': { color: COLOR, fontSize: 14 } }} />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{dh(o.total)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                              {Number(o.avance) > 0 ? dh(o.avance) : '—'}
                            </TableCell>
                            <TableCell align="right"><ResteChip o={o} /></TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Imprimer le devis">
                                <IconButton size="small" onClick={() => printDevis(o)} sx={{ color: COLOR }} aria-label={`Imprimer le devis ${o.reference}`}>
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <PermissionButton moduleName="Comptoir" operation="edit" miniModuleName={MINI} component="IconButton" size="small" tooltipTitle="Ouvrir / modifier" onClick={() => openEdit(o)}>
                                <EditIcon fontSize="small" />
                              </PermissionButton>
                              <PermissionButton moduleName="Comptoir" operation="delete" miniModuleName={MINI} component="IconButton" size="small" color="error" tooltipTitle="Supprimer" onClick={() => handleDelete(o)}>
                                <DeleteIcon fontSize="small" />
                              </PermissionButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <CustomTablePagination
                  count={total} page={page} rowsPerPage={rowsPerPage}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
              </>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            {(deletedQuery.data ?? []).length === 0 && !deletedQuery.isLoading ? (
              <EmptyState icon={<DeleteIcon />} title="La corbeille est vide" text="Les offres supprimées apparaissent ici et peuvent être restaurées." />
            ) : (
              <TableContainer sx={getModuleTableTheme('comptoir')}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>N° d'offre</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Supprimée le</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(deletedQuery.data ?? []).map((o) => (
                      <TableRow key={o.id}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.secondary' }}>{o.reference}</TableCell>
                        <TableCell>{clientCell(o)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{dh(o.total)}</TableCell>
                        <TableCell>{o.deleted_at ? new Date(o.deleted_at).toLocaleString('fr-FR') : '—'}</TableCell>
                        <TableCell align="center">
                          <PermissionButton moduleName="Comptoir" operation="edit" miniModuleName={MINI} size="small" startIcon={<RestoreIcon />} onClick={() => handleRestore(o)} sx={{ color: COLOR }}>
                            Restaurer
                          </PermissionButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      <OffreWizard
        open={wizardOpen}
        offre={editing}
        onClose={() => setWizardOpen(false)}
        onSaved={(o, isNew, print) => {
          setWizardOpen(false);
          invalidate();
          if (print) printDevis(o); // the preview itself confirms the save
          else showSuccess(
            `${isNew ? 'Offre créée' : 'Offre enregistrée'} : ${o.reference} — ${o.client_nom}. Total ${dh(o.total)}, reste à payer ${dh(o.reste)}.`,
          );
        }}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* Devis preview: print / download from the viewer toolbar */}
      {pdfPreviewElement}
      <SuppressionDialog
        offre={suppression?.offre ?? null}
        impact={suppression?.impact ?? null}
        onClose={() => setSuppression(null)}
        onDone={(message) => { setSuppression(null); showSuccess(message); invalidate(); }}
      />
    </Box>
  );
};

export default ComptoirOffres;
